import React from 'react';
import { Box, Typography } from '@mui/material';
import { CONTROL, PRES, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState } from '../presentationSettings';

interface TableDefaultsPreviewProps {
  settings: PresentationSettingsState;
}

function colorHex(settings: PresentationSettingsState, id: string, fallback: string): string {
  return settings.colors.find((color) => color.id === id)?.hex || fallback;
}

const TableDefaultsPreview: React.FC<TableDefaultsPreviewProps> = ({ settings }) => {
  const cols = Math.max(1, Math.min(4, settings.tableDefaults.cols || 1));
  const rows = Math.max(2, Math.min(4, settings.tableDefaults.rows || 2));
  const header = colorHex(settings, settings.tableDefaults.headerBgColorId, '#1B3967');
  const grid = colorHex(settings, settings.tableDefaults.gridColorId, '#998A6B');
  const zebra = colorHex(settings, settings.tableDefaults.zebraColorId, '#F5F0E8');
  const fontSize = Math.max(10, Math.min(18, settings.tableDefaults.fontSize || 14));

  return (
    <Box>
      <Typography sx={{ mb: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.label }}>
        {COPY.settingsTablePreview}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(56px, 1fr))`,
          border: `1px solid ${grid}`,
          borderRadius: `${CONTROL.radius}px`,
          overflow: 'hidden',
          maxWidth: 420,
        }}
      >
        {Array.from({ length: rows }).map((_, rowIndex) => (
          Array.from({ length: cols }).map((__, colIndex) => {
            const headerRow = rowIndex === 0;
            const zebraRow = !headerRow && rowIndex % 2 === 0;
            return (
              <Box
                key={`${rowIndex}-${colIndex}`}
                sx={{
                  minHeight: 28,
                  px: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: headerRow ? header : zebraRow ? zebra : PRES.surface,
                  color: headerRow ? PRES.white : PRES.textPrimary,
                  borderRight: colIndex < cols - 1 ? `1px solid ${grid}` : 0,
                  borderBottom: rowIndex < rows - 1 ? `1px solid ${grid}` : 0,
                  fontFamily: TYPE.ui,
                  fontSize,
                }}
              >
                {headerRow ? COPY.settingsTablePreviewHeader : COPY.settingsTablePreviewCell}
              </Box>
            );
          })
        ))}
      </Box>
    </Box>
  );
};

export default TableDefaultsPreview;
